import { createElement, type ReactNode } from "react";

/**
 * A block of markdown, before inline formatting is resolved.
 *
 * Kept separate from rendering so it is testable as data — a table either
 * parsed into `header`/`rows` or it did not, and that can be checked without
 * a React runtime. See the verification script this ticket asked for.
 */
export type MarkdownBlock =
  | { kind: "heading"; level: number; text: string }
  | { kind: "unordered"; items: string[] }
  | { kind: "ordered"; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] }
  | { kind: "paragraph"; text: string };

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UNORDERED_RE = /^\s*[-*]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/;

export function isTableSeparatorLine(line: string | undefined): boolean {
  if (line === undefined) return false;
  const trimmed = line.trim();
  if (!trimmed.includes("|") && !trimmed.includes("-")) return false;
  const cells = trimmed.replace(/^\|/, "").replace(/\|$/, "").split("|");
  return (
    cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell.trim()))
  );
}

export function splitRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((cell) => cell.trim());
}

/**
 * Markdown, as a sequence of blocks — headings, lists, a table, paragraphs.
 *
 * Line-based on purpose: the fixtures use exactly headings, bold, ordered
 * and unordered lists, tables and paragraphs (this is FR-DOC-2's actual
 * content, not an invented sample), and a parser that covers only that is
 * under a hundred lines. A commonmark implementation would be substantial
 * unused surface for a ~40kB dependency this feature does not need.
 */
export function parseMarkdownBlocks(markdown: string): MarkdownBlock[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line === undefined) break;

    if (line.trim() === "") {
      i++;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const [, hashes, text] = heading;
      blocks.push({
        kind: "heading",
        level: (hashes ?? "#").length,
        text: (text ?? "").trim(),
      });
      i++;
      continue;
    }

    if (line.includes("|") && isTableSeparatorLine(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length) {
        const rowLine = lines[i];
        if (
          rowLine === undefined ||
          rowLine.trim() === "" ||
          !rowLine.includes("|")
        ) {
          break;
        }
        rows.push(splitRow(rowLine));
        i++;
      }
      blocks.push({ kind: "table", header, rows });
      continue;
    }

    const unordered = UNORDERED_RE.exec(line);
    if (unordered) {
      const items = [(unordered[1] ?? "").trim()];
      i++;
      for (;;) {
        const next = lines[i];
        const match = next === undefined ? null : UNORDERED_RE.exec(next);
        if (!match) break;
        items.push((match[1] ?? "").trim());
        i++;
      }
      blocks.push({ kind: "unordered", items });
      continue;
    }

    const ordered = ORDERED_RE.exec(line);
    if (ordered) {
      const items = [(ordered[1] ?? "").trim()];
      i++;
      for (;;) {
        const next = lines[i];
        const match = next === undefined ? null : ORDERED_RE.exec(next);
        if (!match) break;
        items.push((match[1] ?? "").trim());
        i++;
      }
      blocks.push({ kind: "ordered", items });
      continue;
    }

    const paragraph = [line.trim()];
    i++;
    for (;;) {
      const next = lines[i];
      if (
        next === undefined ||
        next.trim() === "" ||
        HEADING_RE.test(next) ||
        UNORDERED_RE.test(next) ||
        ORDERED_RE.test(next) ||
        (next.includes("|") && isTableSeparatorLine(lines[i + 1]))
      ) {
        break;
      }
      paragraph.push(next.trim());
      i++;
    }
    blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
  }

  return blocks;
}

/**
 * Bold (`**text**`), inline code (`` `text` ``) and a link (`[text](url)`) —
 * the emphasis the fixtures use, plus code: a "readable document" styles
 * code distinctly from prose the moment a page has any, and a span is cheap
 * enough to support ahead of a fixture actually needing it, unlike a fenced
 * code *block* (its own `MarkdownBlock` kind, its own line-scanning loop in
 * `parseMarkdownBlocks`) — real weight for a kind no fixture page uses.
 *
 * A link opens in a new tab (#390's "clicking them in reading mode opens
 * them"): this reader sits inside the ERP shell, and a same-tab navigation
 * away from it is not a click a reader can undo with a back button that
 * still has the page on it.
 */
/**
 * The URL a document link may point at, or `null` to render it as plain text.
 *
 * A page is written by one user and read by everyone with access to its
 * folder, so a link is untrusted input. Only web, mail and in-app targets are
 * let through: `javascript:` and `data:` hrefs would run script, or render
 * attacker HTML, for every reader who clicked them.
 */
export function safeHref(href: string): string | null {
  if (href.startsWith("/") || href.startsWith("#")) return href;
  try {
    const { protocol } = new URL(href);
    return protocol === "http:" ||
      protocol === "https:" ||
      protocol === "mailto:"
      ? href
      : null;
  } catch {
    return null;
  }
}

/**
 * How an image's address becomes something an `<img>` may load, or `null` to
 * show its alt text instead (#628).
 *
 * `/docs/attachments/<id>` is a page attachment (a pasted screenshot): the
 * caller maps it to the authenticated download URL, since the API is another
 * origin. An `https:` address is let through as is. Nothing else — a
 * `javascript:` or `data:` source is refused the same way a link is.
 */
export type ImageResolver = (href: string) => string | null;

const ATTACHMENT_HREF_RE = /^\/docs\/attachments\/([^/?#\s]+)$/;

/** The attachment id inside a `/docs/attachments/<id>` address, if it is one. */
export function attachmentIdFromHref(href: string): string | null {
  return ATTACHMENT_HREF_RE.exec(href)?.[1] ?? null;
}

/** The resolver a screen builds from the data source's `attachmentUrl`. */
export function imageResolver(
  attachmentUrl: (id: string) => string,
): ImageResolver {
  return (href) => {
    const id = attachmentIdFromHref(href);
    if (id !== null) return attachmentUrl(id);
    return /^https:\/\//i.test(href) ? href : null;
  };
}

/** The markdown a pasted image becomes: `![name](/docs/attachments/<id>)`. */
export function imageMarkdown(alt: string, attachmentId: string): string {
  const safeAlt = alt.replace(/[[\]\r\n]/g, " ").trim();
  return `![${safeAlt}](/docs/attachments/${attachmentId})`;
}

function renderInline(
  text: string,
  key: string,
  resolveImage?: ImageResolver,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  const spanRe =
    /!\[([^\]]*)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|`([^`]+?)`|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let lastIndex = 0;
  let count = 0;
  let match: RegExpExecArray | null;

  while ((match = spanRe.exec(text)) !== null) {
    const [whole, imageAlt, imageHref, bold, code, linkText, linkHref] = match;
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (imageHref !== undefined) {
      const src = resolveImage?.(imageHref) ?? null;
      nodes.push(
        src === null
          ? (imageAlt ?? "")
          : createElement("img", {
              key: `${key}-i${count}`,
              src,
              alt: imageAlt ?? "",
              loading: "lazy",
              className: "my-2 block h-auto max-w-full rounded-md border",
            }),
      );
    } else if (bold !== undefined) {
      nodes.push(createElement("strong", { key: `${key}-b${count}` }, bold));
    } else if (code !== undefined) {
      nodes.push(
        createElement(
          "code",
          {
            key: `${key}-c${count}`,
            className: "bg-muted rounded px-1 py-0.5 font-mono text-[0.85em]",
          },
          code,
        ),
      );
    } else if (linkHref === undefined || safeHref(linkHref) === null) {
      nodes.push(linkText ?? "");
    } else {
      nodes.push(
        createElement(
          "a",
          {
            key: `${key}-l${count}`,
            href: safeHref(linkHref) ?? undefined,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-primary-strong underline underline-offset-2",
          },
          linkText,
        ),
      );
    }
    count++;
    lastIndex = match.index + (whole?.length ?? 0);
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

const HEADING_CLASS: Record<number, string> = {
  1: "text-xl font-semibold",
  2: "text-lg font-semibold",
  3: "text-base font-semibold",
};

/**
 * One block to React elements.
 *
 * No explicit return type: leaving it inferred keeps the switch honest —
 * every case below returns an element, so a case added to `MarkdownBlock`
 * without a matching branch here leaves the function's end reachable with
 * nothing to return, which fails the build rather than rendering nothing.
 */
function renderBlock(
  block: MarkdownBlock,
  index: number,
  resolveImage?: ImageResolver,
) {
  const key = `b${index}`;

  switch (block.kind) {
    case "heading": {
      const level = Math.min(Math.max(block.level, 1), 6);
      const tag = `h${level}`;
      return createElement(
        tag,
        { key, className: HEADING_CLASS[level] ?? "text-sm font-semibold" },
        renderInline(block.text, key, resolveImage),
      );
    }
    case "unordered":
      return createElement(
        "ul",
        { key, className: "list-disc space-y-1 pl-5" },
        block.items.map((item, itemIndex) =>
          createElement(
            "li",
            { key: `${key}-${itemIndex}` },
            renderInline(item, `${key}-${itemIndex}`, resolveImage),
          ),
        ),
      );
    case "ordered":
      return createElement(
        "ol",
        { key, className: "list-decimal space-y-1 pl-5" },
        block.items.map((item, itemIndex) =>
          createElement(
            "li",
            { key: `${key}-${itemIndex}` },
            renderInline(item, `${key}-${itemIndex}`, resolveImage),
          ),
        ),
      );
    case "table":
      return createElement(
        "div",
        { key, className: "overflow-x-auto rounded-md border" },
        createElement(
          "table",
          { className: "w-full border-collapse text-sm" },
          createElement(
            "thead",
            null,
            createElement(
              "tr",
              { className: "bg-muted/50 border-b" },
              block.header.map((cell, cellIndex) =>
                createElement(
                  "th",
                  {
                    key: `${key}-h${cellIndex}`,
                    className: "px-3 py-2 text-left font-medium",
                  },
                  renderInline(cell, `${key}-h${cellIndex}`, resolveImage),
                ),
              ),
            ),
          ),
          createElement(
            "tbody",
            null,
            block.rows.map((row, rowIndex) =>
              createElement(
                "tr",
                {
                  key: `${key}-r${rowIndex}`,
                  className: "border-b last:border-b-0",
                },
                row.map((cell, cellIndex) =>
                  createElement(
                    "td",
                    {
                      key: `${key}-r${rowIndex}-c${cellIndex}`,
                      className: "px-3 py-2 align-top",
                    },
                    renderInline(
                      cell,
                      `${key}-r${rowIndex}-c${cellIndex}`,
                      resolveImage,
                    ),
                  ),
                ),
              ),
            ),
          ),
        ),
      );
    case "paragraph":
      return createElement(
        "p",
        { key },
        renderInline(block.text, key, resolveImage),
      );
  }
}

/**
 * Render one page's markdown as React elements.
 *
 * No `dangerouslySetInnerHTML` anywhere in this file — every node is built
 * with `createElement`, so a heading, a bold span or a table cell can never
 * be interpreted as markup. The day this content comes from a real author
 * instead of a fixture is the day that distinction is the difference
 * between a paragraph and a stored XSS.
 */
export function renderMarkdown(
  markdown: string,
  resolveImage?: ImageResolver,
): ReactNode {
  const blocks = parseMarkdownBlocks(markdown);
  return createElement(
    "div",
    { className: "max-w-none space-y-4 text-[15px] leading-7" },
    blocks.map((block, index) => renderBlock(block, index, resolveImage)),
  );
}

/** Case, spacing and trailing punctuation set aside: what a title says. */
function sameTitle(a: string, b: string): boolean {
  const norm = (text: string) =>
    text
      .trim()
      .replace(/\s+/g, " ")
      .replace(/[.:]+$/, "")
      .toLowerCase();
  return norm(a) === norm(b);
}

/**
 * Whether a page's body opens with its own title as a `#` heading (#641).
 * Most pages do, which drew the title twice: once as the page heading and
 * again as the first line of the text.
 */
export function opensWithTitle(markdown: string, title: string): boolean {
  const first = markdown.split("\n").find((line) => line.trim() !== "");
  const heading = first ? /^#\s+(.+?)\s*#*\s*$/.exec(first.trim()) : null;
  return heading?.[1] !== undefined && sameTitle(heading[1], title);
}

/**
 * Where the text after an opening "# Title" line starts (#641): the editor's
 * cursor goes there, so the live preview draws the title as a heading. Zero
 * when the page does not open with its title.
 */
export function bodyStart(markdown: string, title: string): number {
  if (!opensWithTitle(markdown, title)) return 0;
  const match = /^\s*#[^\n]*\n(?:[ \t]*\n)*/.exec(markdown);
  return match ? match[0].length : 0;
}

/** The body with that opening heading taken off, for the reading view. */
export function withoutOpeningTitle(markdown: string, title: string): string {
  if (!opensWithTitle(markdown, title)) return markdown;
  const lines = markdown.split("\n");
  const at = lines.findIndex((line) => line.trim() !== "");
  return lines
    .slice(at + 1)
    .join("\n")
    .replace(/^\s*\n/, "");
}
